import { useState, useEffect } from "react";
import { Platform } from "react-native";
import { getMicrophonePreference, setSystemDefaultMicrophone } from "../utils/microphoneStorage";

interface MicrophoneDevice {
  deviceId: string;
  label: string;
  groupId?: string;
}

export const useMicrophoneDevices = () => {
  const [devices, setDevices] = useState<MicrophoneDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getMicrophoneDevices = async () => {
    if (Platform.OS !== "web") {
      setDevices([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      stream.getTracks().forEach((track) => track.stop());

      const audioDevices = await navigator.mediaDevices.enumerateDevices();
      const microphoneDevices = audioDevices
        .filter((device) => device.kind === "audioinput")
        .map((device) => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${device.deviceId.slice(0, 8)}...`,
          groupId: device.groupId,
        }));

      setDevices(microphoneDevices);

      const savedDeviceId = await getMicrophonePreference();

      if (
        savedDeviceId &&
        savedDeviceId !== "default" &&
        microphoneDevices.find((device) => device.deviceId === savedDeviceId)
      ) {
        setSelectedDeviceId(savedDeviceId);
        await setSystemDefaultMicrophone(savedDeviceId);
      } else if (microphoneDevices.length > 0) {
        const firstDeviceId = microphoneDevices[0].deviceId;
        setSelectedDeviceId(firstDeviceId);
        await setSystemDefaultMicrophone(firstDeviceId);
      }

      navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);
    } catch (err) {
      setError("Failed to access microphone devices. Please check your permissions.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeviceChange = async () => {
    await getMicrophoneDevices();
  };

  const selectDevice = async (deviceId: string) => {
    setSelectedDeviceId(deviceId);
    await setSystemDefaultMicrophone(deviceId);
  };

  useEffect(() => {
    getMicrophoneDevices();

    return () => {
      if (Platform.OS === "web") {
        navigator.mediaDevices.removeEventListener("devicechange", handleDeviceChange);
      }
    };
  }, []);

  return {
    devices,
    selectedDeviceId,
    selectedDevice: devices.find((device) => device.deviceId === selectedDeviceId),
    isLoading,
    error,
    selectDevice,
    refreshDevices: getMicrophoneDevices,
  };
};
